import { selectWarehouse } from '../Warehouses/warehousesSlice'
import useWarehousesLoading from 'hooks/useWarehousesLoading'
import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import WarehouseSlides from './WarehouseSlides'
import MagNumber from 'components/MagNumber'
import { isMobile } from 'utils/isMobile'
import MagPromotion from './MagPromotion'
import { useReduxSelector } from 'store'
import MagActions from './MagActions'
import MagHistory from './MagHistory'
import MagAccess from './MagAccess'
import MagStatus from './MagStatus'
import MagManage from './MagManage'
import './style.scss'

const mobile = isMobile()

const statuses = <MagStatus />
const promotion = <MagPromotion />

export const SECTION_TYPES = {
  access: 'access',
  cancel: 'cancel',
  details: 'details',
  history: 'history',
  invoices: 'invoices',
  extension: 'extension',
  accessories: 'accessories',
  termination: 'termination'
}

const Warehouse: React.FC = () => {

  const { push, replace, location: { pathname, search } } = useHistory()
  const { id } = useParams<warehouse.PageParams>()

  const { name, contractId, isShared, acronym, permissions, sharing, events } = useReduxSelector(state => selectWarehouse(state, id))
  const [expanded, expand] = useState(!!search)
  useWarehousesLoading(!contractId)

  const menuRef = useRef<HTMLDivElement>(null)
  const accessRef = useRef<HTMLDivElement>(null)
  const manageRef = useRef<HTMLDivElement>(null)
  const historyRef = useRef<HTMLDivElement>(null)
  const warehouseRef = useRef<HTMLDivElement>(null)

  const scrolledUp = useRef(true)

  useEffect(() => {
    if (mobile) {
      document.body.style.setProperty('overflow', 'hidden')
      return () => {
        document.body.style.removeProperty('overflow')
      }
    }
  }, [])

  useEffect(() => {
    if (search) {
      const query = new URLSearchParams(search)
      const from = query.get('from')

      if (from) {
        scroll(from === SECTION_TYPES.history ? historyRef
          : from === SECTION_TYPES.access ? accessRef
            : manageRef, 'auto')
        replace(pathname)
      }
    }
  }, [pathname, replace, search])

  const onTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    const menu = menuRef.current
    if (!menu || !mobile) return

    const initialY = e.touches[0].clientY
    const touchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY
      if ((initialY - currentY > 40 && !expanded) || (currentY - initialY > 40 && expanded)) {
        expand(!expanded)
        touchEnd()
      }
    }

    const touchEnd = () => {
      menu.removeEventListener('touchend', touchEnd, false)
      menu.removeEventListener('touchmove', touchMove, false)
    }

    menu.addEventListener('touchend', touchEnd, false)
    menu.addEventListener('touchmove', touchMove, false)
  }

  const scrollPage = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop } = e.target as HTMLDivElement
    if (!expanded && scrollTop > 160) expand(true)
    else if (expanded && scrollTop < 20) expand(false)
  }

  const scrollMenu = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop } = e.target as HTMLDivElement
    scrolledUp.current = scrollTop < 1
  }

  const scroll = (ref: React.RefObject<HTMLElement>, behavior: ScrollBehavior = 'smooth') => {
    ref.current?.scrollIntoView({ behavior })
  }

  const goTo = (path: string, previousPath?: string) => {
    replace(`${pathname}?from=${previousPath || path}`)
    push(`${pathname}/${path}`)
  }

  const toggleMenu = () => {
    if (menuRef.current && expanded) menuRef.current.scrollTop = 0
    mobile ? expand(!expanded) : scroll(expanded ? warehouseRef : menuRef)
  }

  return (
    <div
      className='warehouse'
      onScroll={e => !mobile && scrollPage(e)}
      style={{ overflow: mobile ? 'hidden' : 'auto' }}>
      <div
        ref={warehouseRef}
        className='warehouse__main center'>
        <MagNumber
          number={name}
          isShared={isShared}
          acronym={acronym}
        />
        <WarehouseSlides id={id} />
      </div>

      <div
        ref={menuRef}
        className='menu__main'
        style={(expanded && mobile) ? { top: isShared ? 15 : 0 } : undefined}
        onTouchStart={e => (mobile && scrolledUp.current) && onTouch(e)}>

        <div
          className='menu__full'
          onTouchStart={e => mobile && onTouch(e)}>
          {statuses}
        </div>

        <div
          onScroll={e => mobile && scrollMenu(e)}
          style={{ overflowY: expanded ? 'auto' : 'hidden' }}>

          <MagActions
            id={id}
            expanded={expanded}
            toggleMenu={toggleMenu} />

          {(expanded || !mobile) && <>
            <div ref={manageRef}>
              <MagManage
                id={id}
                goTo={path => {
                  goTo(path, SECTION_TYPES.details)
                }}
              />
            </div>
            {(Array.isArray(events) && events.length > 0 && (!isShared || permissions?.includes('events'))) && <div ref={historyRef}>
              <MagHistory
                id={id}
                more={() => {
                  goTo(SECTION_TYPES.history)
                }}
              />
            </div>}
            {Array.isArray(sharing) && sharing.length > 0 && (!isShared || permissions?.includes('share')) && <div ref={accessRef}>
              <MagAccess more={() => {
                goTo(SECTION_TYPES.access)
              }} />
            </div>}
            {promotion}
          </>}
        </div>
      </div>
    </div>
  )
}

export default Warehouse